import { AnimatePresence } from 'framer-motion';
import { GetServerSideProps } from 'next';

import { x } from '@/style';

import { Carousel } from '@ui-furniture/common/carousel';
import { Em, Text, VStack } from '@ui-furniture/elements';
import { useParam } from '@ui-furniture/hooks/use-param';
import { ChildAnimation } from '@ui-furniture/ui/animation/child';
import { BreakpointContainer } from '@ui-furniture/ui/breakpoint-container';
import { Page } from '@ui-furniture/ui/layout/page';
import {
  RadioButtons,
  useRadioButtonControls,
} from '@ui-furniture/ui/radio-buttons';
import { TabContent } from '@ui-furniture/ui/tabs/tab-content';

import { surveyData } from '../common/survey-data';
import {
  SurveyRecommendationProvider,
  useSurveyRecommendation,
} from '../context/survey-recommendation';
import { GeneralSurveyCard } from '../ui/general-survey-card';
import { GeneralSurveyDescriptionColumn } from '../ui/general-survey-description-column';
import { RecommendationForm } from '../ui/recommendation-form';
import { SpecialistSurveyCard } from '../ui/specialist-survey-card';

const OrganiseSurveyContent = () => {
  const type = useParam('type', 'string', { isOptional: true }) || 'general';

  const { carouselRef, surveysId } = useSurveyRecommendation();
  const radioControls = useRadioButtonControls({
    options: [
      {
        label: 'General Surveys',
        value: 'general',
      },
      {
        label: 'Specialist Reports',
        value: 'specialist',
      },
    ],
    defaultValue: type,
  });

  return (
    <Page maxW='content-width-lg'>
      <VStack gap='xl'>
        <VStack>
          <ChildAnimation>
            <VStack>
              <Text variant='h3' as='h1' textAlign='center'>
                Organise a <Em>Survey</Em>
              </Text>
            </VStack>
          </ChildAnimation>
          <ChildAnimation>
            <VStack maxW='content-width-xs' mx='auto' textAlign='center'>
              <Text>
                Tell us some information about the property and we will give you
                an instant quote for any property survey. Then, you can book the
                survey and have it completed within just 11 days.
              </Text>
            </VStack>
          </ChildAnimation>
        </VStack>
        <RecommendationForm />
        <AnimatePresence>
          <VStack transition='base' gap='2xl' mt='2xl' id={surveysId}>
            <RadioButtons controls={radioControls} />
            <TabContent
              tab={radioControls.selected}
              tabs={{
                general: (
                  <VStack w='100%'>
                    <ChildAnimation>
                      <Text
                        variant='bodySmall'
                        maxW='content-width-sm'
                        textAlign='center'
                        mx='auto'
                      >
                        This is the main survey for your property, which every
                        homebuyer should get as a minimum. Although not a legal
                        requirement, it is crucial you find out if there is
                        something wrong with the property before you become the
                        legal owner. Plus, all mortgage lenders require a survey
                        to be done before they will approve your mortgage.
                      </Text>
                    </ChildAnimation>

                    <BreakpointContainer
                      breakpoint='largeTablet'
                      whenSmaller={
                        <ChildAnimation maxW='content-width-sm' mx='auto'>
                          <Carousel
                            ref={carouselRef}
                            settings={{
                              slidesToShow: 1,
                              initialSlide: 1,
                              dots: true,
                              infinite: true,
                            }}
                          >
                            {surveyData.generalSurveys.map((survey) => (
                              <x.div
                                key={survey.level}
                                h='100%'
                                mt='xl'
                                mb='lg'
                                px='sm'
                              >
                                <x.div
                                  display={'grid'}
                                  gridTemplateColumns={1}
                                  gridTemplateRows={'repeat(21, auto)'}
                                >
                                  <GeneralSurveyCard showText {...survey} />
                                </x.div>
                              </x.div>
                            ))}
                          </Carousel>
                        </ChildAnimation>
                      }
                      whenLarger={
                        <x.div
                          mt={'2xl'}
                          display={'grid'}
                          gridTemplateColumns={4}
                          gridTemplateRows={'repeat(21, auto)'}
                          columnGap='lg'
                        >
                          <GeneralSurveyDescriptionColumn />
                          {surveyData.generalSurveys.map((survey) => (
                            <ChildAnimation
                              key={survey.name}
                              display={'grid'}
                              gridTemplateRows='subgrid'
                              gridRow='1 / -1'
                              mb='lg'
                            >
                              <GeneralSurveyCard
                                key={survey.level}
                                {...survey}
                              />
                            </ChildAnimation>
                          ))}
                        </x.div>
                      }
                    />
                  </VStack>
                ),
                specialist: (
                  <VStack w='100%'>
                    <VStack mb='xl'>
                      <ChildAnimation>
                        <Text
                          variant='bodySmall'
                          maxW='content-width-sm'
                          textAlign='center'
                          mx='auto'
                        >
                          These reports are more detailed inspections of your
                          new home. Usually these will be needed if your first
                          survey has indicated a problem in one of these areas
                          that needs further investigation. If you have any
                          suspicions, it is advised that you order a specialist
                          report so that there are no nasty surprises down the
                          line.
                        </Text>
                      </ChildAnimation>
                    </VStack>
                    <x.div
                      display='grid'
                      gridTemplateColumns={{ _: 1, largePhone: 2, tablet: 3 }}
                      gap={4}
                    >
                      {surveyData.specialistSurveys.map((survey) => (
                        <ChildAnimation
                          key={survey.name}
                          display='grid'
                          gridTemplateColumns='subgrid'
                          gridTemplateRows='subgrid'
                        >
                          <SpecialistSurveyCard {...survey} />
                        </ChildAnimation>
                      ))}
                    </x.div>
                  </VStack>
                ),
              }}
            />
          </VStack>
        </AnimatePresence>
      </VStack>
    </Page>
  );
};

export const OrganiseSurveyView = () => {
  return (
    <SurveyRecommendationProvider>
      <OrganiseSurveyContent />
    </SurveyRecommendationProvider>
  );
};

/**
 * use SSR so that the param is immediately ready
 */
export const getServerSideProps: GetServerSideProps = async () => ({
  props: {},
});
